import React from "react";
import Button from "../../common/Button/Button";
import Icon from "../../common/Icon/Icon";
import { preserveSelection, useRichTextEditor } from "../../form/RichTextEditor/RichTextEditor";
import { AttributePopover } from "./AttributePopover";
import { AttributePopoverAnchor } from "./AttributePopoverAnchor";
import { attributeTokenContent } from "./tiptap/attributeToken";
import type { AttributeOption } from "./types";

export interface AttributeInsertControlProps {
  attributes: AttributeOption[];
  groupOrder?: string[];
}

/**
 * `RichTextEditor` toolbar control (ENG-591): a "+ Insert attribute" button that
 * opens the shared `AttributePopover`. Picking an attribute inserts an inline
 * `attributeToken` node at the cursor. Follows the editor's `disabled` flag.
 */
export const AttributeInsertControl = ({ attributes, groupOrder }: AttributeInsertControlProps): React.ReactElement => {
  const { editor, disabled } = useRichTextEditor();
  const [open, setOpen] = React.useState(false);

  const insert = (attribute: AttributeOption) => {
    editor?.chain().focus().insertContent(attributeTokenContent(attribute.key)).run();
    setOpen(false);
  };

  return (
    <AttributePopoverAnchor
      open={open}
      onOpenChange={setOpen}
      placement="bottom-end"
      anchor={
        <Button
          label="Insert attribute"
          variant="outline"
          size="sm"
          selected={open}
          disabled={!editor || disabled}
          onMouseDown={preserveSelection}
          onClick={() => setOpen((current) => !current)}
        >
          <Icon
            name="add"
            size={14}
          />
          Insert attribute
        </Button>
      }
    >
      <AttributePopover
        hint="Insert attribute at current location…"
        attributes={attributes}
        groupOrder={groupOrder}
        onPick={insert}
      />
    </AttributePopoverAnchor>
  );
};

export default AttributeInsertControl;
